import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { addMonths, addWeeks, eachDayOfInterval, endOfMonth, endOfWeek, format, isSameMonth, isToday, startOfMonth, startOfWeek } from "date-fns";
import { de } from "date-fns/locale";
import { CalendarDays, ChevronLeft, ChevronRight, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { EmptyState, PageHeader } from "../components/ui-bits";
import { Button } from "../components/ui/button";
import { CalendarEventForm } from "../components/SimpleForms";

const TYPE_COLORS = {
  project_deadline: { label: "Projekt-Deadline", color: "#B88A4A" },
  task_due: { label: "Aufgabe fällig", color: "#3B82F6" },
  payment: { label: "Zahlung", color: "#22C55E" },
  meeting: { label: "Kundentermin", color: "#A855F7" },
  deadline: { label: "Frist", color: "#EF4444" },
  other: { label: "Termin", color: "#6B7280" },
};

export default function CalendarPage() {
  const [cursor, setCursor] = useState(new Date());
  const [view, setView] = useState("month");
  const [events, setEvents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const navigate = useNavigate();

  const range = useMemo(() => {
    if (view === "week") {
      const s = startOfWeek(cursor, { weekStartsOn: 1 });
      return { from: format(s, "yyyy-MM-dd"), to: format(addWeeks(s, 1), "yyyy-MM-dd") };
    }
    const s = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
    const e = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
    return { from: format(s, "yyyy-MM-dd"), to: format(e, "yyyy-MM-dd") };
  }, [cursor, view]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/calendar", { params: { date_from: range.from, date_to: range.to } });
      setEvents(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/projects");
        setProjects(data);
      } catch {}
    })();
  }, []);

  const byDate = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      (map[e.date] = map[e.date] || []).push(e);
    });
    return map;
  }, [events]);

  const days = useMemo(() => {
    if (view === "week") {
      const s = startOfWeek(cursor, { weekStartsOn: 1 });
      return eachDayOfInterval({ start: s, end: addWeeks(s, 1) }).slice(0, 7);
    }
    return eachDayOfInterval({
      start: startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 }),
      end: endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 }),
    });
  }, [cursor, view]);

  const move = (dir) => setCursor((c) => (view === "week" ? addWeeks(c, dir) : addMonths(c, dir)));

  const removeEvent = async (e) => {
    if (!e.id.startsWith("event-")) return;
    if (!window.confirm(`Termin „${e.title}" löschen?`)) return;
    try {
      await api.delete(`/calendar/events/${e.id.replace("event-", "")}`);
      toast.success("Termin gelöscht");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const openEvent = (e) => {
    if (e.link) navigate(e.link);
  };

  const agenda = useMemo(
    () => Object.entries(byDate).sort(([a], [b]) => a.localeCompare(b)),
    [byDate]
  );

  return (
    <div className="animate-fade-up" data-testid="calendar-page">
      <PageHeader overline="Planung" title="Kalender" testid="calendar-header">
        <div className="flex rounded-xl border border-[#1D225C]/10 bg-white p-1 shadow-sm">
          {[["month", "Monat"], ["week", "Woche"], ["agenda", "Agenda"]].map(([v, l]) => (
            <button
              key={v}
              data-testid={`calendar-view-${v}`}
              onClick={() => setView(v)}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${view === v ? "bg-[#1D225C] text-white" : "text-[#1D225C]/60 hover:text-[#1D225C]"}`}
            >
              {l}
            </button>
          ))}
        </div>
        <Button data-testid="new-event-button" onClick={() => setFormOpen(true)} className="rounded-xl bg-[#B88A4A] text-white hover:bg-[#a67a3f]">
          <Plus className="mr-1.5 h-4 w-4" /> Termin
        </Button>
      </PageHeader>

      <div className="mb-4 flex items-center gap-3">
        <button data-testid="calendar-prev" onClick={() => move(-1)} className="rounded-xl border border-[#1D225C]/10 bg-white p-2 shadow-sm hover:border-[#B88A4A]/50">
          <ChevronLeft className="h-4 w-4 text-[#1D225C]" />
        </button>
        <h2 className="min-w-48 text-center font-display text-lg font-bold capitalize text-[#1D225C]" data-testid="calendar-title">
          {view === "week"
            ? `KW ${format(startOfWeek(cursor, { weekStartsOn: 1 }), "I", { locale: de })} · ${format(cursor, "MMMM yyyy", { locale: de })}`
            : format(cursor, "MMMM yyyy", { locale: de })}
        </h2>
        <button data-testid="calendar-next" onClick={() => move(1)} className="rounded-xl border border-[#1D225C]/10 bg-white p-2 shadow-sm hover:border-[#B88A4A]/50">
          <ChevronRight className="h-4 w-4 text-[#1D225C]" />
        </button>
        <button data-testid="calendar-today" onClick={() => setCursor(new Date())} className="text-xs font-semibold text-[#B88A4A] hover:underline">
          Heute
        </button>
        <div className="ml-auto hidden flex-wrap gap-3 lg:flex">
          {Object.entries(TYPE_COLORS).map(([k, v]) => (
            <span key={k} className="flex items-center gap-1.5 text-[11px] text-[#777]">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: v.color }} /> {v.label}
            </span>
          ))}
        </div>
      </div>

      {view !== "agenda" ? (
        <div className="overflow-hidden rounded-2xl border border-[#1D225C]/10 bg-white shadow-sm" data-testid="calendar-grid">
          <div className="grid grid-cols-7 border-b border-[#1D225C]/10 bg-[#F7F6F2]">
            {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map((d) => (
              <div key={d} className="px-2 py-2 text-center text-[11px] font-bold uppercase tracking-wide text-[#777]">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {days.map((day) => {
              const key = format(day, "yyyy-MM-dd");
              const dayEvents = byDate[key] || [];
              const outside = view === "month" && !isSameMonth(day, cursor);
              return (
                <div
                  key={key}
                  data-testid={`calendar-day-${key}`}
                  className={`min-h-24 border-b border-r border-[#1D225C]/5 p-1.5 ${outside ? "bg-[#F7F6F2]/50" : ""} ${isToday(day) ? "bg-[#B88A4A]/5" : ""}`}
                >
                  <p className={`mb-1 text-right text-[11px] font-semibold ${isToday(day) ? "text-[#B88A4A]" : outside ? "text-[#bbb]" : "text-[#1D225C]"}`}>
                    {format(day, "d")}
                  </p>
                  <div className="space-y-1">
                    {dayEvents.slice(0, 3).map((e) => (
                      <button
                        key={e.id}
                        data-testid={`calendar-event-${e.id}`}
                        onClick={() => openEvent(e)}
                        className="block w-full truncate rounded-md px-1.5 py-0.5 text-left text-[10px] font-semibold text-white"
                        style={{ backgroundColor: TYPE_COLORS[e.type]?.color || "#6B7280" }}
                        title={e.title}
                      >
                        {e.time ? `${e.time} ` : ""}{e.title}
                      </button>
                    ))}
                    {dayEvents.length > 3 && <p className="px-1 text-[10px] text-[#999]">+{dayEvents.length - 3} weitere</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div data-testid="calendar-agenda">
          {agenda.length === 0 ? (
            <EmptyState icon={CalendarDays} title="Keine Termine" hint="In diesem Zeitraum gibt es keine Deadlines oder Termine." testid="calendar-empty" />
          ) : (
            <div className="space-y-4">
              {agenda.map(([date, list]) => (
                <div key={date}>
                  <p className="mb-2 text-xs font-bold uppercase tracking-[0.15em] text-[#B88A4A]">
                    {format(new Date(date + "T12:00:00"), "EEEE, d. MMMM yyyy", { locale: de })}
                  </p>
                  <div className="overflow-hidden rounded-2xl border border-[#1D225C]/10 bg-white shadow-sm">
                    {list.map((e, i) => (
                      <div key={e.id} className={`flex items-center gap-3 px-4 py-3 ${i ? "border-t border-[#1D225C]/5" : ""}`} data-testid={`agenda-event-${e.id}`}>
                        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: TYPE_COLORS[e.type]?.color || "#6B7280" }} />
                        <button onClick={() => openEvent(e)} className="min-w-0 flex-1 text-left text-sm font-medium text-[#1D225C] hover:text-[#B88A4A]">
                          {e.time ? `${e.time} · ` : ""}{e.title}
                        </button>
                        <span className="text-[11px] text-[#999]">{TYPE_COLORS[e.type]?.label}</span>
                        {e.id.startsWith("event-") && (
                          <button data-testid={`agenda-delete-${e.id}`} onClick={() => removeEvent(e)} className="rounded p-1 text-[#999] hover:text-red-500">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <CalendarEventForm open={formOpen} onOpenChange={setFormOpen} projects={projects} onSaved={load} />
    </div>
  );
}
